import { useEffect, useState } from "react";
import { Columns3, Save, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/** 导出字段设置 — per-user column picker for quote Excel / PDF exports,
 *  opened from the 报价单 page (left of 新建). */
export function ExportColumnsDialog({ open, onClose }) {
  const { t } = useLanguage();
  const [available, setAvailable] = useState([]);
  const [locked, setLocked] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/export-columns").then(({ data }) => {
      setAvailable(data.available || []);
      setLocked(data.locked || []);
      setSelected(data.selected || []);
    }).catch(() => {});
  }, [open]);

  const toggle = (col) => {
    if (locked.includes(col)) return;
    setSelected((prev) => (prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/export-columns", { columns: selected });
      setSelected(data.selected);
      toast.success(t("exportCols.saved"));
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="export-columns-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Columns3 size={15} /> {t("exportCols.title")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">{t("exportCols.hint")}</p>
        <div className="flex flex-wrap gap-2">
          {available.map((col) => {
            const on = selected.includes(col);
            const isLocked = locked.includes(col);
            return (
              <button key={col} type="button" onClick={() => toggle(col)} disabled={isLocked}
                className={`px-2.5 py-1 rounded-md border text-xs font-mono transition-colors ${
                  on ? "bg-brand/15 border-brand text-brand" : "bg-background border-border text-muted-foreground hover:border-brand/50"
                } ${isLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid={`export-col-${col.replace(/[^A-Za-z0-9]/g, "_")}`}>
                {col}{isLocked ? " 🔒" : ""}
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setSelected(available)} data-testid="export-cols-all">
            {t("exportCols.all")}
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="export-cols-save">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {t("exportCols.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
